import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { Intent } from '../../@types/intent'
import { RealEstateType } from '../../@types/realEstateType'
import { useReportViewEvent } from '../../hooks'
import { WizardFlowType } from '../../@types/wizardFlowType'
import { Loader } from '../../components/styled/Loader/StyledLoader'
import { ReduxState } from '../../store/reducers'
import './PleaseWaitStep.scss'
import { fetchValuation } from '../../store/valuation/thunks'
import { STEP } from '../../@types/state'

interface PleaseWaitStepProps {
  goToNextStep: () => void
}

const DEFAULT_TIMEOUT = 1000

const createTimeout = (onTimeout: () => void, timeout: number) => {
  setTimeout(() => {
    onTimeout()
  }, timeout)
}

const extendedBuyerVerificationSteps = [
  'Datencheck',
  'Standortcheck',
  'Eigenkapital',
  'Monatliche Einnahmen',
  'Beschäftigungsverhältnis',
]

export const HEADLINE_FOR_SOLAR_FLOW = 'Anbieter werden verglichen...'

export const PleaseWaitStep: FC<PleaseWaitStepProps> = (props) => {
  const {
    config: { theme },
    flow: { wizardFlowType: flowType },
    lead: { realEstateType, intent, extendedBuyerLead },
    location: { data: location },
  } = useSelector((state: ReduxState) => state)
  const dispatch = useDispatch()

  useEffect(() => {
    if (realEstateType != RealEstateType.COMMERCIAL) {
      dispatch(fetchValuation({ valuationForStep: STEP.ADDRESS }))
    }
  }, [])

  const [lastVerifiedStep, setLastVerifiedStep] = useState(0)
  const [lastVerifiedStepExtendedBuy, setLastVerifiedStepExtendedBuy] = useState(0)

  const verificationSteps =
    flowType === WizardFlowType.SOLAR
      ? ['Datencheck', 'Anbietercheck', 'Angebotserstellung']
      : ['Datencheck', 'Standortcheck', 'Bewertung']

  const timeOutForVerificationSteps = () => {
    if (intent === Intent.BUY && extendedBuyerLead) {
      extendedBuyerVerificationSteps.forEach((_, index) => {
        const step = index + 1
        createTimeout(() => setLastVerifiedStepExtendedBuy(step), step * DEFAULT_TIMEOUT)
      })
      createTimeout(props.goToNextStep, DEFAULT_TIMEOUT * (extendedBuyerVerificationSteps.length + 1))
    } else {
      verificationSteps.forEach((_, index) => {
        const step = index + 1
        createTimeout(() => setLastVerifiedStep(step), step * DEFAULT_TIMEOUT)
      })
      createTimeout(props.goToNextStep, DEFAULT_TIMEOUT * (verificationSteps.length + 1))
    }
  }

  useEffect(() => {
    timeOutForVerificationSteps()
  }, [])

  useReportViewEvent({ pageTitle: 'loading' })

  const getHeadline = () => {
    if (flowType === WizardFlowType.SOLAR) return HEADLINE_FOR_SOLAR_FLOW
    switch (intent) {
      case Intent.RENT:
      case Intent.INDEPENDENT_SALE:
        return realEstateType === RealEstateType.LAND
          ? 'Wert des Grundstücks wird eingeschätzt'
          : 'Wert der Immobilie wird eingeschätzt'
      case Intent.INDEPENDENT_WITH_OFFER_SALE:
        return 'Passende Käufer werden gesucht'
      case Intent.PARTIAL_SALE:
        return 'Teilverkauf wird berechnet'
      case Intent.BUY: {
        if (extendedBuyerLead) return 'Wir berechnen, was du dir leisten kannst'
        else if (realEstateType === RealEstateType.LAND) return 'Wert des Grundstücks wird eingeschätzt'
        else return 'Wert der Immobilie wird eingeschätzt'
      }
      case Intent.MAKLER_LET:
      case Intent.MAYBE_MAKLER_LET:
        return `Passende Experten in ${location?.city} werden gesucht`
      case Intent.INDEPENDENT_LET:
        return 'Wert der Immobilie wird eingeschätzt'
      default:
        return `Passende Makler in ${
          [WizardFlowType.IBW, WizardFlowType.RLE].includes(flowType) ? `${location?.city}` : `Ihrer Region`
        } werden gesucht`
    }
  }

  const verificationStep = (step: string, verified: boolean) => (
    <div className='grid-item margin-top-xs wait-step' key={step}>
      <span className='grid-item three-fourth font-m align-left'>{step}</span>
      <i
        className={`grid-item one-fourth is24-icon-positive-check font-xl align-right ${
          !verified ? 'non-verified-icon' : ''
        }`}
        aria-hidden='true'
      />
    </div>
  )

  return (
    <StepTemplate
      headline={getHeadline()}
      icon={<div />}
      displayNext={false}
      backgroundColor={theme.background}
      textColor={theme.text}
      secondaryLight={theme.secondaryLight}
      secondary={theme.secondary}
    >
      <div className='grid-flex grid-justify-center padding-bottom-xl'>
        <div className='grid-item'>
          <Loader color={theme.secondary} />
        </div>
      </div>
      <div className='grid-flex grid-justify-center margin-top-xxl'>
        <div className='grid-item verification-container'>
          {intent === Intent.BUY && extendedBuyerLead
            ? extendedBuyerVerificationSteps.map((step, index) =>
                verificationStep(step, lastVerifiedStepExtendedBuy >= index + 1)
              )
            : verificationSteps.map((step, index) => verificationStep(step, lastVerifiedStep >= index + 1))}
        </div>
      </div>
    </StepTemplate>
  )
}

export default PleaseWaitStep
